import React, { useEffect, useState } from "react";
import {
  createBuffet,
  deleteBuffet,
  fetchBuffets,
  getCurrency,
  updateBuffet,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Buffet catalog — managers create buffet "levels" much like menu items, but
// with a free-text definition (what's included) instead of a recipe, and a
// per-head price. Used only inside group orders; never shown to walk-in guests
// (QR menu) or in Captain Order.
const empty = () => ({ name: "", level: "", per_head_price: "", definition: "", active: true });

export default function BuffetsPage() {
  const { t } = useT();
  const cur = getCurrency() || "ETB";
  const money = (v) => formatPrice(v || 0, cur);
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty());
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    fetchBuffets().then(setList).catch((e) => setErr(e?.response?.data?.detail || "Could not load."))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const edit = (b) => {
    setEditId(b.id);
    setForm({ name: b.name || "", level: b.level ?? "", per_head_price: b.per_head_price ?? "", definition: b.definition || "", active: b.active !== false });
    setMsg(""); setErr("");
  };
  const reset = () => { setEditId(null); setForm(empty()); };

  const save = async () => {
    if (!form.name.trim()) { setErr(`${t("buf_name")} *`); return; }
    setErr(""); setMsg("");
    const body = {
      name: form.name.trim(),
      level: form.level === "" ? null : Number(form.level),
      per_head_price: Number(form.per_head_price) || 0,
      definition: form.definition,
      active: !!form.active,
    };
    try {
      if (editId) await updateBuffet(editId, body); else await createBuffet(body);
      setMsg(t("buf_saved")); reset(); load();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not save."); }
  };

  const remove = async (b) => {
    if (!window.confirm(t("buf_confirm_delete"))) return;
    try { await deleteBuffet(b.id); if (editId === b.id) reset(); load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not save."); }
  };

  return (
    <>
      <h1>🍲 {t("buf_nav")}</h1>
      <p className="muted" style={{ marginTop: -4, maxWidth: 640 }}>{t("buf_intro")}</p>
      {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}

      <div className="card" style={{ marginTop: 12, maxWidth: 640 }}>
        <h3 style={{ marginTop: 0 }}>{editId ? form.name || t("buf_nav") : t("buf_new")}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12 }}>
          <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("buf_name")}<br /><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ width: "100%" }} /></label>
          <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("buf_level")}<br /><input type="number" min="1" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} style={{ width: "100%" }} /></label>
          <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("buf_price")}<br /><input type="number" min="0" step="0.01" value={form.per_head_price} onChange={(e) => setForm({ ...form, per_head_price: e.target.value })} style={{ width: "100%" }} /></label>
        </div>
        <label style={{ fontSize: 12, color: "#5f6b78", display: "block", marginTop: 12 }}>{t("buf_def")}<br />
          <textarea rows={3} value={form.definition} onChange={(e) => setForm({ ...form, definition: e.target.value })} placeholder={t("buf_def_ph")} style={{ width: "100%" }} />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#5f6b78", marginTop: 10 }}>
          <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> {t("buf_active")}
        </label>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12 }}>
          <button className="btn" onClick={save}>{t("buf_save")}</button>
          {editId && <button className="btn secondary" onClick={reset}>+ {t("buf_new")}</button>}
          {msg && <span style={{ color: "#1D9E75", fontWeight: 600, fontSize: 14 }}>{msg}</span>}
        </div>
      </div>

      {loading ? <p className="muted" style={{ marginTop: 12 }}>{t("loading")}</p> : list.length === 0 ? (
        <p className="muted" style={{ marginTop: 12 }}>{t("buf_none")}</p>
      ) : (
        <div style={{ marginTop: 12, display: "grid", gap: 8, maxWidth: 640 }}>
          {list.map((b) => (
            <div key={b.id} className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <span>
                <div style={{ fontWeight: 700 }}>{b.name} {b.level != null && <span className="muted" style={{ fontSize: 13 }}>· L{b.level}</span>} {b.active === false && <span className="muted" style={{ fontSize: 12 }}>(off)</span>}</div>
                {b.definition && <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{b.definition}</div>}
              </span>
              <span style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <div style={{ fontWeight: 700, color: "#063b48" }}>{money(b.per_head_price)}<span className="muted" style={{ fontSize: 11, fontWeight: 400 }}>/pax</span></div>
                <div style={{ marginTop: 6, display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  <button className="btn secondary small" onClick={() => edit(b)}>✎</button>
                  <button className="btn secondary small" onClick={() => remove(b)}>✕</button>
                </div>
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
